import {getLinkData} from 'Application/utils/linkFieldUtils';

import type {LinkField} from 'types/payload-types';

/**
 * A custom hook that extracts and normalizes properties for rendering a footer link.
 * This hook processes a `LinkField` object and returns the attributes required to render
 * a proper anchor element. It resolves the `href` using `getHrefForLinkType`, determines
 * if the link should be treated as a download, and sets the `rel` and `target` attributes
 * according to the link type. External and file links are automatically opened in a new tab
 * (`_blank`), while other link types follow the default behavior.
 *
 * @param link The link configuration object containing metadata such as label, type, and target.
 * @returns An object with normalized link properties: `href`, `isDownload`, `label`, `rel`, and `target`.
 *
 * @example
 * ```tsx
 * const { href, target, label } = useFooterLink({
 *   label: 'Privacy Policy',
 *   linkType: 'external',
 *   url: 'https://example.com/privacy'
 * });
 *
 * // href: 'https://example.com/privacy'
 * // target: '_blank'
 * // label: 'Privacy Policy'
 * ```
 */
export const useFooterLink = (link: LinkField) => {
    const {href, isDownload, label, rel, target} = getLinkData(link);

    return {
        href,
        isDownload,
        label,
        rel,
        target
    };
};